import { Fragment, type ReactNode } from 'react'

import { FormattedNumber, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  Hazmat,
  WeightInPounds,
  QueryLoadingManager,
} from '@b-stock/fe-core/bstock-next/components'
import {
  inventoryGroupsQuery,
  isFulfillmentListing,
  type LeanGroup,
  type OrchestratedAuctionListingDetails,
  type OrchestratedListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { ShipmentEnums } from '@b-stock/fe-core/locale'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import {
  CardGrid,
  CellSmallValue,
  CellValue,
  SuccessCellValue,
  type CardGridItem,
} from '@/buyer-portal/components/layouts/CardLayout/card/CardGrid'

import {
  renderDimensionsContent,
  renderPalletSpacesContent,
  renderWeightContent,
  useSellerCollectsShipping,
  useShipmentDetails,
  type ShipmentDimensionRow,
} from './useShipmentDetails'

const NoteValue = styled(CellValue)`
  ${Typography.Body3}
  white-space: pre-line;
`

const ShipmentDetailsGrid = ({
  listing,
  groups,
  hazmatOnly,
}: {
  listing: OrchestratedListingDetails
  groups: LeanGroup[]
  hazmatOnly: boolean
}) => {
  const { formatMessage } = useIntl()
  const {
    allowedShipmentTypes,
    columnVisibility,
    dimensionRows,
    firstGroup,
    isFloorLoaded,
    isAllPallets,
    isHazardous,
    note,
    numberOfUnits,
    showNumberOfUnits,
    showOnlyMessage,
    showPalletSpaces,
    totalPalletSpaces,
    totalWeightInGrams,
    transportMode,
    truckCount,
  } = useShipmentDetails(listing, groups)
  const sellerCollectsShipping = useSellerCollectsShipping(listing)

  const joinRows = (render: (row: ShipmentDimensionRow) => ReactNode) =>
    dimensionRows.map((row, index) => (
      <Fragment key={row.key}>
        {index > 0 ? ' + ' : ''}
        {render(row)}
      </Fragment>
    ))

  const items: CardGridItem[] = []

  if (!hazmatOnly) {
    if (isFloorLoaded) {
      if (showPalletSpaces) {
        items.push({
          id: 'palletSpaces',
          label: (
            <FormattedMessage id="Auction.shipment.details.palletSpaces" />
          ),
          value: (
            <CellValue>
              <FormattedNumber value={totalPalletSpaces} />
            </CellValue>
          ),
        })
      }
    } else if (dimensionRows.length > 0) {
      items.push({
        id: 'dimensions',
        label: <FormattedMessage id="Auction.shipment.details.dimensions" />,
        value: (
          <CellSmallValue data-testid="Dimensions">
            {joinRows((row) => renderDimensionsContent(row.groupType, row.dim))}
          </CellSmallValue>
        ),
      })
      if (columnVisibility.weight) {
        items.push({
          id: 'weight',
          label: <FormattedMessage id="Auction.shipment.details.weight" />,
          value: (
            <CellValue data-testid="Weight">
              {joinRows((row) => renderWeightContent(row.dim))}
            </CellValue>
          ),
        })
      }
      if (columnVisibility.palletSpaces) {
        items.push({
          id: 'spaces',
          label: (
            <FormattedMessage id="Auction.shipment.details.palletSpaces" />
          ),
          value: (
            <CellValue data-testid="PalletSpaces">
              {joinRows((row) => renderPalletSpacesContent(row.palletSpaces))}
            </CellValue>
          ),
        })
      }
    }

    if (firstGroup?.type) {
      items.push({
        id: 'packagingType',
        label: <FormattedMessage id="Auction.shipment.details.packagingType" />,
        value: (
          <CellValue>
            <FormattedMessage id={`Auction.GroupType.${firstGroup.type}`} />
          </CellValue>
        ),
      })
    }

    items.push({
      id: 'shipmentType',
      label: <FormattedMessage id="Auction.shipment.details.shipmentType" />,
      value: (
        <CellValue>
          {allowedShipmentTypes
            .map((type) =>
              ShipmentEnums.ShipmentType.format(formatMessage, type)
            )
            .join(', ')}
          {showOnlyMessage && <FormattedMessage id="ShippingAvailable.only" />}
        </CellValue>
      ),
    })

    // Who owns freight, stated per storefront. B-Stock is not always the
    // answer on a Seller Collects lot, and the grid previously implied it was.
    if (sellerCollectsShipping.isSellerCollects) {
      items.push({
        id: 'freightArrangedBy',
        label: (
          <FormattedMessage id="Listing.shipping.sellerCollects.arrangedByLabel" />
        ),
        value: (
          <CellValue data-testid="freight-arranged-by">
            <FormattedMessage
              id={sellerCollectsShipping.arrangedById}
              values={{ sellerName: sellerCollectsShipping.sellerName }}
            />
          </CellValue>
        ),
      })
      items.push({
        id: 'freightCollectedBy',
        label: (
          <FormattedMessage id="Listing.shipping.sellerCollects.collectedByLabel" />
        ),
        value: (
          <CellValue data-testid="freight-collected-by">
            <FormattedMessage
              id={sellerCollectsShipping.collectedById}
              values={{
                hasFee: sellerCollectsShipping.hasFee ? 'true' : 'false',
                sellerName: sellerCollectsShipping.sellerName,
              }}
            />
          </CellValue>
        ),
      })
    }

    if (transportMode) {
      items.push({
        id: 'transportMode',
        label: <FormattedMessage id="Auction.shipment.details.transportMode" />,
        value: (
          <SuccessCellValue>
            <FormattedMessage
              id={`Auction.shipment.details.transportModes.${transportMode}`}
            />
          </SuccessCellValue>
        ),
      })
    }

    items.push({
      id: 'numberOfShipments',
      label: (
        <FormattedMessage id="Auction.shipment.details.numberOfShipmentsShort" />
      ),
      value: <CellValue>1</CellValue>,
    })

    if (showNumberOfUnits) {
      items.push({
        id: 'numberOfUnits',
        label: (
          <FormattedMessage
            id={
              isAllPallets
                ? 'Auction.shipment.details.numberOfPalletsShort'
                : 'Auction.shipment.details.numberOfUnitsShort'
            }
          />
        ),
        value: (
          <CellValue data-testid="numberOfUnits">
            <FormattedNumber value={numberOfUnits} />
          </CellValue>
        ),
      })
    }

    if (totalWeightInGrams) {
      items.push({
        id: 'shipmentWeight',
        label: (
          <FormattedMessage id="Auction.shipment.details.shipmentWeight" />
        ),
        value: (
          <CellValue>
            <WeightInPounds grams={totalWeightInGrams} />
          </CellValue>
        ),
      })
    }

    items.push({
      id: 'truckCount',
      label: <FormattedMessage id="Auction.shipment.details.truckCountShort" />,
      value: (
        <CellValue>
          <FormattedNumber value={truckCount ?? 1} />
        </CellValue>
      ),
    })

    if (note) {
      items.push({
        id: 'note',
        isFullWidth: true,
        label: (
          <FormattedMessage id="Auction.shipment.details.additionalInformation" />
        ),
        value: <NoteValue>{note}</NoteValue>,
      })
    }
  }

  if (isHazardous) {
    items.push({
      id: 'hazmat',
      isFullWidth: true,
      value: <Hazmat listing={listing} topBorder={!hazmatOnly} />,
    })
  }

  return <CardGrid items={items} />
}

const ShipmentDetailsCardContent = ({
  listing,
  hazmatOnly,
}: {
  listing: OrchestratedListingDetails
  hazmatOnly: boolean
}) => {
  const inventoryGroupsQueryResult = inventoryGroupsQuery.useQuery(
    listing.lot._id
  )

  return (
    <AccordionCard
      title={<FormattedMessage id="Auction.title.shipmentDetails" />}
    >
      <QueryLoadingManager {...inventoryGroupsQueryResult}>
        {({ data }) =>
          data?.groups ? (
            <ShipmentDetailsGrid
              listing={listing}
              groups={data.groups}
              hazmatOnly={hazmatOnly}
            />
          ) : null
        }
      </QueryLoadingManager>
    </AccordionCard>
  )
}

export const ShipmentDetailsCard = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const hazmatOnly =
    listing.listingType === 'CONTRACT' &&
    !!listing.shipping?.hazmat?.isHazardous

  if (
    !isFulfillmentListing(listing) &&
    listing.listingType !== 'SPOT' &&
    !hazmatOnly
  ) {
    return null
  }

  return (
    <ShipmentDetailsCardContent listing={listing} hazmatOnly={hazmatOnly} />
  )
}
